import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMonthlyNGN,
  getMonthlyUSD,
  getTotalNGN,
  getTotalUSD,
  getUserStatistics,
  orderStats
} from '../redux/selectors';
import commaNumber from 'comma-number';
import { get_order_stat, get_user_statistics } from '../redux/reducer';
import { AreaChartComponent } from './area-chart-container';

const ChartRevenue = () => {
  const [currency, setCurrency] = useState('NGN');
  const [type, setType] = useState('monthlyUser');
  const totalNGN = useSelector(getTotalNGN);
  const totalUSD = useSelector(getTotalUSD);
  const monthlyUSD = useSelector(getMonthlyUSD);
  const monthlyNGN = useSelector(getMonthlyNGN);
  const user_statistics = useSelector(getUserStatistics);
  const order = useSelector(orderStats);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(get_user_statistics());
    dispatch(get_order_stat());
  }, [dispatch]);

  const userStat = useMemo(() => {
    if (!user_statistics) return;
    const months = [
      '',
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    let arr = [];
    months.forEach((item, i) => {
      const match = user_statistics?.monthlyUsers?.find((item2) => {
        return i === item2.month;
      });
      let data = {
        name: item,
        amt: user_statistics?.totalUsers,
        pv: match?.total || 0,
        uv: match?.total || 0
      };
      arr.push(data);
    });
    const filter = arr.filter((data) => {
      return data.name !== '';
    });
    return filter;
  }, [user_statistics]);

  const orderStat = useMemo(() => {
    if (!order) return;
    const months = [
      '',
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    let arr = [];
    months.forEach((item, i) => {
      const match = order?.orderStats?.find((item2) => {
        return i === item2._id;
      });
      let data = {
        name: item,
        amt: order?.totalOrders,
        pv: match?.total || 0,
        uv: match?.total || 0
      };
      arr.push(data);
    });
    return arr;
  }, [order]);

  //   console.log(orderStat)

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl shadow-[0px_1px_2px_0px_#0000000A,0px_1px_3px_0px_#0000000F] flex-1 font-outfit">
      <div className="flex justify-between gap-3 lg:items-center px-5 py-4 border-b border-[#F3F4F6] flex-col lg:flex-row">
        <div>
          <p className="text-base text-[#111827] font-semibold">Revenue Stats</p>
          <p className="text-sm text-[#9CA3AF] font-normal">
            All Time ·{' '}
            {currency === 'NGN'
              ? `${totalNGN.symbol}${commaNumber(totalNGN.price)} · ${
                  totalNGN.total
                } transactions`
              : `${totalUSD.symbol}${commaNumber(totalUSD.price)} · ${
                  totalUSD.total
                } transactions`}
          </p>
        </div>

        <div className="flex flex-row gap-2">
          <div className="flex bg-[#F5F6FA] p-[3px] rounded-[8px]">
            {['Users', 'Revenue'].map((item) => {
              const isActive =
                type === 'monthlyUser' ? item === 'Users' : item === 'Revenue';
              return (
                <div
                  onClick={() =>
                    setType(item === 'Users' ? 'monthlyUser' : 'monthlyRevenue')
                  }
                  className={`px-3 py-1.5 cursor-pointer text-sm font-medium transition-all duration-200 ease-in-out rounded-[6px] ${
                    isActive
                      ? 'text-[#111827] bg-white shadow-[0px_1px_2px_0px_#0000000A,0px_1px_3px_0px_#0000000F]'
                      : 'bg-transparent text-[#9CA3AF]'
                  }`}
                >
                  {item}
                </div>
              );
            })}
          </div>
          <div className="flex gap-1 items-center">
            {['NGN', 'USD'].map((item) => {
              const isActive = item === currency;
              return (
                <div
                  onClick={() => setCurrency(item)}
                  key={item}
                  className={`font-semibold text-[11px] px-2.5 py-2 rounded-[6px] cursor-pointer duration-200 transition-all ease-in-out border ${
                    isActive
                      ? 'text-white bg-[#003232] border-[#003232]'
                      : 'text-[#9CA3AF] bg-white border-[#E5E7EB]'
                  }`}
                >
                  {item}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CHART */}
      <div className="px-[18px] py-3.5 flex gap-3.5 flex-col lg:flex-row">
        <div className="flex-1">
          <AreaChartComponent
            type={type}
            userStat={userStat}
            orderStat={orderStat}
          />
        </div>
        <div className="shrink-0 w-full lg:w-[150px] border-l border-[#F3F4F6] px-4">
          <div className="border-b border-[#F3F4F6] pb-3">
            <p className="text-[#9CA3AF] text-[10px] uppercase">This Month</p>
            <p className="text-[#111827] text-lg font-bold">
              {currency === 'NGN'
                ? `${totalNGN.symbol}${commaNumber(monthlyNGN.price)}`
                : `${totalUSD.symbol}${commaNumber(monthlyUSD.price)}`}
            </p>
            <p className="text-[10px] text-[#9CA3AF] font-normal">
              {currency === 'NGN'
                ? `${commaNumber(monthlyNGN.total)} transactions`
                : `${commaNumber(monthlyUSD.total)} transactions`}{' '}
            </p>
          </div>

          <div className="pt-3">
            <p className="text-[#9CA3AF] text-[10px] uppercase">All Time</p>
            <p className="text-[#111827] text-lg font-bold">
              {currency === 'NGN'
                ? `${totalNGN.symbol}${commaNumber(totalNGN.price)}`
                : `${totalUSD.symbol}${commaNumber(totalUSD.price)}`}
            </p>
            <p className="text-[10px] text-[#9CA3AF] font-normal">
              {currency === 'NGN'
                ? `${commaNumber(totalNGN.total)} transactions`
                : `${commaNumber(totalUSD.total)} transactions`}{' '}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartRevenue;
